import { Terminal, Mail } from "lucide-react";
import { FaDiscord, FaTelegram, FaVk, FaSteam } from "react-icons/fa";
import MatrixRain from "@/components/MatrixRain";
import { Card, CardContent } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import LiveClock from "@/components/LiveClock";
import InteractiveContactCard from "@/components/InteractiveContactCard";
import FloatingParticles from "@/components/FloatingParticles";
import { socials } from "@/data/gamedev-data";

const Contacts = () => {
  return (
    <div className="min-h-screen bg-background relative">
      <MatrixRain />
      <Navigation />

      {/* Hero Section */}
      <section className="relative py-20 pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 cyber-gradient opacity-20"></div>
        <div className="absolute inset-0 hex-pattern"></div>
        
        {/* ASCII Art Background */}
        <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none scale-150">
          <pre className="font-terminal text-xs text-primary leading-tight">
{`
   ██████╗ ██████╗ ███╗   ██╗████████╗ █████╗  ██████╗████████╗███████╗
  ██╔════╝██╔═══██╗████╗  ██║╚══██╔══╝██╔══██╗██╔════╝╚══██╔══╝██╔════╝
  ██║     ██║   ██║██╔██╗ ██║   ██║   ███████║██║        ██║   ███████╗
  ██║     ██║   ██║██║╚██╗██║   ██║   ██╔══██║██║        ██║   ╚════██║
  ╚██████╗╚██████╔╝██║ ╚████║   ██║   ██║  ██║╚██████╗   ██║   ███████║
   ╚═════╝ ╚═════╝ ╚═╝  ╚═══╝   ╚═╝   ╚═╝  ╚═╝ ╚═════╝   ╚═╝   ╚══════╝
`}
          </pre>
        </div>
        
        <FloatingParticles />
        <div className="container mx-auto px-4 max-w-3xl relative z-10">
          <div className="text-center mb-8">
            <p className="text-sm font-mono text-primary/50 mb-6 tracking-widest uppercase">CONTACTS</p>
            <h1 className="text-6xl md:text-7xl font-bold font-cyber mb-6 mt-32">
              <span className="text-accent">&lt;</span>
              <span className="neon-gradient bg-clip-text text-transparent">
                КОНТАКТЫ
              </span>
              <span className="text-accent">/&gt;</span>
            </h1>
            <p className="font-mono text-foreground/70 text-lg mb-4">
              <span className="text-secondary">{'// '}</span>Свяжитесь с нами для обсуждения проектов
            </p>
            <p className="font-terminal text-primary animate-pulse text-xl">
              {'> '} МЫ_НА_СВЯЗИ {'_'}
            </p>
          </div>
        </div>
      </section>

      <section className="relative py-10">
        <div className="container mx-auto px-4 max-w-3xl relative z-10">
          <div className="cyber-card p-10 text-center space-y-8">
            
            <Card className="cyber-card">
              <CardContent className="p-8 space-y-6">
                
                <div>
                  <p className="text-sm text-primary font-mono mb-4">{'>> СОЦИАЛЬНЫЕ_СЕТИ'}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Discord студии */}
                    <a
                      href={socials.discord.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cyber-card p-4 text-center hover:border-primary transition-all group"
                      title={socials.discord.label}
                    >
                      <FaDiscord className="w-6 h-6 text-primary mx-auto mb-2 transition-colors" />
                      <div className="text-sm text-primary mb-1">{'<Discord/>'}</div>
                      <div className="font-terminal text-xs text-foreground/70">{socials.discord.handle}</div>
                    </a>
                    
                    {/* Telegram студии */}
                    <a
                      href={socials.telegram.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cyber-card p-4 text-center hover:border-accent transition-all group"
                      title={socials.telegram.label}
                    >
                      <FaTelegram className="w-6 h-6 text-accent mx-auto mb-2 transition-colors" />
                      <div className="text-sm text-accent mb-1">{'<Telegram/>'}</div>
                      <div className="font-terminal text-xs text-foreground/70">{socials.telegram.handle}</div>
                    </a>
                    
                    {/* VK студии */}
                    <a
                      href={socials.vk.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cyber-card p-4 text-center hover:border-secondary transition-all group"
                      title={socials.vk.label}
                    >
                      <FaVk className="w-6 h-6 text-secondary mx-auto mb-2 transition-colors" />
                      <div className="text-sm text-secondary mb-1">{'<VK/>'}</div>
                      <div className="font-terminal text-xs text-foreground/70">{socials.vk.handle}</div>
                    </a>
                    
                    {/* Steam студии */}
                    <a
                      href={socials.steam.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cyber-card p-4 text-center hover:border-primary transition-all group"
                      title={socials.steam.label}
                    >
                      <FaSteam className="w-6 h-6 text-primary mx-auto mb-2 transition-colors" />
                      <div className="text-sm text-primary mb-1">{'<Steam/>'}</div>
                      <div className="font-terminal text-xs text-foreground/70">{socials.steam.handle}</div>
                    </a>
                  </div>
                </div>
                
                <div className="h-px bg-gradient-to-r from-transparent via-primary to-transparent my-6"></div>
                
                {/* Live Clock Component */}
                <LiveClock />
                
                <div className="h-px bg-gradient-to-r from-transparent via-accent to-transparent my-6"></div>
                
                <div className="cyber-card p-6 bg-background/50 text-left">
                  <p className="text-secondary font-mono text-sm mb-3">{'>> ЧАСЫ_РАБОТЫ'}</p>
                  <div className="space-y-2 text-base font-mono text-foreground/80">
                    <div><span className="text-primary">&gt;</span> Понедельник - Пятница: 10:00 - 20:00</div>
                    <div><span className="text-accent">&gt;</span> Суббота - Воскресенье: 12:00 - 18:00</div>
                    <div><span className="text-secondary">&gt;</span> Часовой пояс: Омск (UTC+6)</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="cyber-card p-6 bg-background/50 text-left space-y-4 mt-8">
              <p className="text-accent font-mono text-sm font-bold">{'>> FAQ'}</p>
              <div className="space-y-3 text-base font-mono text-foreground/80">
                <div>
                  <p className="text-primary mb-1">{'>'} Принимаете ли вы заказы?</p>
                  <p className="pl-4">Нет, мы работаем только над собственными творческими проектами.</p>
                </div>
                <div>
                  <p className="text-secondary mb-1">{'>'} Есть идеи или пожелания?</p>
                  <p className="pl-4">Нас четверо, и мы открыты к интересным предложениям и обсуждениям!</p>
                </div>
                <div>
                  <p className="text-accent mb-1">{'>'} Где можно скачать ваши игры?</p>
                  <p className="pl-4">Информация о релизах публикуется на странице "Игры".</p>
                </div>
                <div>
                  <p className="text-primary mb-1">{'>'} На каких платформах выйдут ваши игры?</p>
                  <p className="pl-4">В первую очередь PC (Steam), затем рассматриваем мобильные платформы и консоли.</p>
                </div>
                <div>
                  <p className="text-secondary mb-1">{'>'} Можно ли стать тестером ваших игр?</p>
                  <p className="pl-4">Да! Следите за анонсами в наших соцсетях - мы регулярно набираем тестеров для закрытых альфа и бета тестов.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FloatingActions />
      <Footer />
    </div>
  );
};

export default Contacts;
